/**
 * The cart shape every draft cart tracking call reads.
 *
 * `groupOrder` is three-state on purpose:
 *   - an object means the cart row has a `group_orders` row,
 *   - `null` means it was selected and has none,
 *   - `undefined` means the query did not select it
 *
 * A modification of a group order arrives with `groupOrder: null` — the modification is a new
 * `orders` row and `group_order` is not copied onto it — so it reports as `standard` with
 * `is_modification: true`.
 */
export type DraftCartTrackingInput = {
  id: string;
  isModification: boolean;
  groupOrder?: object | null;
  items: readonly {quantity: number}[];
};

type GetDraftCartTrackingFieldsParams = {
  cart: DraftCartTrackingInput;
  /** 1-based place in the rendered list, counted across sections. */
  position: number;
  /** Treatment only: the server section key the cart rendered under. */
  section?: string;
};

export const getDraftCartType = ({groupOrder}: Pick<DraftCartTrackingInput, 'groupOrder'>) => {
  if (groupOrder === undefined) return 'unknown' as const;
  return groupOrder === null ? ('standard' as const) : ('group_order' as const);
};

export type DraftCartType = ReturnType<typeof getDraftCartType>;

export const getDraftCartItemCount = ({items}: Pick<DraftCartTrackingInput, 'items'>) =>
  items.reduce((count, item) => count + item.quantity, 0);

const getDraftCartTrackingFields = ({
  cart,
  position,
  section,
}: GetDraftCartTrackingFieldsParams) => ({
  cart_id: cart.id,
  cart_type: getDraftCartType(cart),
  is_modification: cart.isModification,
  item_count: getDraftCartItemCount(cart),
  position,
  ...(section !== undefined && {section}),
});

export type DraftCartTrackingFields = ReturnType<typeof getDraftCartTrackingFields>;

export default getDraftCartTrackingFields;
